import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import HubspotRequestDemoModal from "@shared/HubspotRequestDemoModal";
import AiSummary from "@shared/AiAssistant/AiSummary";
import Button from "@shared/v2/Button";
import Toast from "@shared/Toast";
import { HouseSolidV6, KeySolidV6 } from "@shared/v2/Icomoon";
import viewPhone from "@shared/models/Communications/viewPhone";
import useSimpleNavigation from "@shared/v2/useSimpleNavigation";
import axios from "axios";
import * as actions from "../actions/personDetailActionCreators";
import * as creators from "../../AddMarketReportModal/actions/creators";
import PersonInteractions from "./PersonInteractions";
import PersonInfo from "./PersonInfo";
import AssignedTo from "./assigned_to/AssignedTo";
import Details from "./Details";
import ContactInfo from "./ContactInfo";
import Documents from "./Documents";
import Relationships from "./Relationships";
import Transactions from "./Transactions";
import WebActivity from "./WebActivity";
import HomeApp from "./HomeApp";
import AutoPlans from "./auto_plans/AutoPlans";
import ListingAlertCard from "./listing_alerts/ListingAlertCard";
import MarketReports from "./MarketReports";
import CmaReports from "./CmaReports";
import CardTasks from "./tasks/CardTasks";
import CardAppointments from "./tasks/CardAppointments";
import Projects from "./Projects";
import Mortgage from "./Mortgage";
import AddMarketReportModal from "../../AddMarketReportModal";
import CardNotes from "./notes/CardNotes";
import TimelinesApp from "../../Timelines/components/TimelinesApp";
import PersonDetailsModal from "../../Contacts/PersonDetailsModal";
import ContactMergeModal from "../../ContactMergeModal/ContactMergeModal";
import { useCurrentUser, useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import AgreementsConfirmModal from "./AgreementsConfirmModal";
import Agreements from "./Agreements";
import ReferLenderModal from "./ReferLenderModal";
import { useSuccessMessageOpen } from "../../reducers/personalDetailReducer/personDetailReducer";
import SelectDocTypeModal, { DocButton } from "./modals/SelectDocTypeModal";
import { viewMarketReportApi } from "../../AddMarketReportModal/services";
import useIsEditable from "../shared/useIsEditable";
import { getHasDuplicates } from "../../reducers/contactMergeReducer";
import ForbiddenError from "./ForbiddenError";

const getApiHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "x-csrf-token": window.ReactOnRails.authenticityToken(),
});

const PersonDetail = () => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const { pathname, push } = useSimpleNavigation();
  const editPersonModalOpen = useSelector((state) => state.personDetail.editPersonModalOpen);
  const person = useSelector((state) => state.personDetail.person);
  const error = useSelector((state) => state.personDetail.error);
  const featureFlags = useFeatureFlags();
  const [state, setState] = useState({
    platformCTAOpen: false,
    marketReportsRefreshKey: 0,
  });
  const successMessageOpen = useSuccessMessageOpen();
  const isEditable = useIsEditable();
  const primaryPhoneNumber = useMemo(
    () =>
      (person.included?.filter((i) => i.type === "phone_detail").map((i) => viewPhone(i)) || []).find(
        (i) => i.isPrimary,
      ),
    [person.included],
  );

  const marketReportPerson = useMemo(
    () => ({
      ...person,
      emails: person.included
        ?.filter((i) => i.type === "email_detail")
        .map((i) => ({
          address: i.attributes.value,
          category: i.attributes.detailable_type,
        })),
      addresses: person.included
        ?.filter((i) => i.type === "address" && i.id !== null)
        .map(
          (i) =>
            `${i.attributes?.street_address} ${i.attributes?.city}, ${i.attributes?.locality} ${i.attributes?.postal_code}`,
        ),
    }),
    [person.data],
  );

  const loadData = () => {
    const slug = pathname.split("/").pop();
    dispatch(actions.getPerson(slug));
    dispatch(actions.getNavLinks(slug));
    dispatch(actions.fetchProjects(slug));
  };

  useEffect(() => {
    loadData();
  }, [pathname]);

  useEffect(() => {
    if (currentUser.account.isAdmin && isEditable && person.data) {
      dispatch(getHasDuplicates(person.data.attributes.slug));
    }
  }, [currentUser, isEditable, person.data]);

  const openPanel2 = async (marketReportId) => {
    const onFailure = (err) => dispatch(creators.getMarketReportError({ error: err }));
    const onSuccess = (response) => {
      if (response?.data?.error) {
        return onFailure(response?.data?.error);
      }
      return dispatch(creators.getMarketReportSuccess({ data: response.data }, 0));
    };
    const data = { id: marketReportId, primary_agent_uuid: person.data?.attributes.primary_agent_uuid };

    await viewMarketReportApi({ data, successCb: onSuccess, errorCb: onFailure });

    setState((prevState) => ({
      ...prevState,
      editMarketReportId: marketReportId,
      showAddModal: true,
    }));
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const marketReportIdParam = urlParams.get("market_report_id");
    if (marketReportIdParam && person.data?.attributes.primary_agent_uuid) {
      openPanel2(marketReportIdParam);
    }
  }, [person.data?.attributes.primary_agent_uuid]);

  useEffect(() => {
    if (!person.data?.id) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("edit")) {
      dispatch(actions.openEditPersonModal());
    }
  }, [person.data?.id]);

  useEffect(() => {
    const firstName = person.data?.attributes.first_name || "";
    const lastName = person.data?.attributes.last_name || "";
    document.title = `${firstName} ${lastName}`.trim() || "No Name";
  }, [person.data?.attributes.first_name, person.data?.attributes.last_name]);

  const openPlatformCTA = () => setState((prevState) => ({ ...prevState, platformCTAOpen: true }));

  const closePlatformCTA = () => setState((prevState) => ({ ...prevState, platformCTAOpen: false }));

  const editMarketReport = (id) => {
    setState((prevState) => ({ ...prevState, editMarketReportId: id, showAddModal: !!id }));
  };

  const toggleAddMarketModal = () => {
    dispatch(creators.setTab(0));
    setState((prevState) => ({
      marketReportsRefreshKey: prevState.showAddModal
        ? prevState.marketReportsRefreshKey + 1
        : prevState.marketReportsRefreshKey,
      showAddModal: !prevState.showAddModal,
      editMarketReportId: null,
    }));
  };

  const hiddenUploadInput = useRef(null);
  const [docType, setDocType] = useState("");
  const [openAddAgreementModal, setOpenAddAgreementModal] = useState(false);
  const [openSelectDocModal, setOpenSelectDocModal] = useState(false);
  const [openAgreementModalConfirmation, setOpenAgreementModalConfirmation] = useState(false);
  const [openAgreementTypeModal, setOpenAgreementTypeModal] = useState(false);

  const setDoc = (documentType) => {
    setDocType(documentType);
    if (documentType === "other") {
      hiddenUploadInput.current?.click();
    } else {
      setOpenAddAgreementModal(true);
    }
    setOpenSelectDocModal();
  };

  const getAppointments = () => dispatch(actions.getAppointments(person.data.attributes.uuid));

  const agreementCloseModal = () => {
    setOpenAddAgreementModal(false);
    getAppointments();
  };

  const closeSelectDocModal = () => {
    setOpenAgreementTypeModal(false);
    getAppointments();
  };

  const primaryAgent = person.data?.attributes.primary_agent_uuid;
  const [userMlsIds, setUserMlsIds] = useState([]);
  const [hasApprovedMls, setHasApprovedMls] = useState(false);
  const [loadingReports, setLoadingReports] = useState(true);
  useEffect(() => {
    if (!person.data?.id) return;
    axios
      .get(`/market_reports/user_mlses?primary_agent_uuid=${primaryAgent}`, { headers: getApiHeaders() })
      .then(({ data }) => {
        setUserMlsIds(data?.map((d) => d.value));
        setHasApprovedMls(data.length);
        setLoadingReports(false);
      });
  }, [person.data?.id, state.marketReportsRefreshKey]);

  if (error?.error === "Forbidden") return <ForbiddenError onRefresh={loadData} error={error} />;

  return (
    <div className="tw-overflow-auto tw-h-full tw-relative">
      <AgreementsConfirmModal
        show={openAgreementModalConfirmation}
        handleHide={() => {
          getAppointments();
          setOpenAgreementModalConfirmation(false);
        }}
        handleAddAgreement={() => {
          setOpenAgreementModalConfirmation(false);
          if (docType === "both") {
            setOpenAgreementTypeModal(true);
            return;
          }

          setDoc(docType);
        }}
      />
      <SelectDocTypeModal closeModal={closeSelectDocModal} modalOpen={openAgreementTypeModal}>
        <DocButton
          text="Buyer Agreement"
          doctype="buyer"
          Icon={KeySolidV6}
          onClickFunc={(doc) => {
            setDoc(doc);
            closeSelectDocModal();
          }}
        />
        <DocButton
          text="Seller Agreement"
          doctype="seller"
          Icon={HouseSolidV6}
          onClickFunc={(doc) => {
            setDoc(doc);
            closeSelectDocModal();
          }}
        />
      </SelectDocTypeModal>
      {successMessageOpen && (
        <Toast containerClassName="tw-w-[226px]" text="Referral submitted successfully!" />
      )}
      {featureFlags.lenderReferral && <ReferLenderModal />}
      <div className="tw-max-w-[1650px] tw-mx-auto tw-flex tw-p-[16px] tw-gap-[16px]">
        <div id="person-details" className="tw-min-w-[280px] tw-max-w-[375px] tw-flex-1">
          <PersonInfo />
          <ContactInfo />
          <Details />
          <Relationships />
          <CardNotes />
          <Transactions />
          <Documents
            docType={docType}
            openAddAgreementModal={openAddAgreementModal}
            setDoc={setDoc}
            agreementCloseModal={agreementCloseModal}
            openSelectDocModal={openSelectDocModal}
            setOpenSelectDocModal={setOpenSelectDocModal}
            hiddenUploadInput={hiddenUploadInput}
          />
          <Projects />
        </div>

        <div className="tw-min-w-[654px] tw-flex-1 tw-flex tw-flex-col tw-gap-[16px]">
          {error && (
            <div className="tw-bg-semantic-red-5 tw-p-[12px] tw-flex tw-justify-between tw-gap-[8px]">
              <span>Something went wrong. Please try again.</span> <Button onClick={loadData}>Refresh</Button>
            </div>
          )}

          {featureFlags.gabbiAi && (
            <AiSummary
              leadPersonUuid={person.data?.attributes?.uuid}
              phoneStatus={primaryPhoneNumber?.aiStatus || "missing"}
            />
          )}

          <PersonInteractions
            setOpenAddAgreementModal={(type) => {
              setDocType(type);
              setOpenAgreementModalConfirmation(true);
            }}
          />

          <div className="tw-shadow-tdp-card col-xs-12">
            <TimelinesApp newDetailsPage userMlsIds={userMlsIds} />
          </div>
        </div>

        <div className="tw-min-w-[280px] tw-max-w-[375px] tw-flex-1">
          <AssignedTo />
          {featureFlags.lenderReferral && <Mortgage />}
          <WebActivity openPlatformCTA={openPlatformCTA} />
          <Agreements />
          <CardAppointments />
          <HomeApp />
          <CardTasks
            setOpenAddAgreementModal={(type) => {
              setDocType(type);
              setOpenAgreementModalConfirmation(true);
            }}
          />
          <AutoPlans openPlatformCTA={openPlatformCTA} />
          <ListingAlertCard openPlatformCTA={openPlatformCTA} />
          <MarketReports
            refreshKey={state.marketReportsRefreshKey}
            editMarketReport={(id) => editMarketReport(id)}
            editMarketReportPanel2={openPanel2}
            openMarketReport={() => toggleAddMarketModal()}
            hasApprovedMls={hasApprovedMls}
            loading={loadingReports}
          />
          <CmaReports hasApprovedMls={hasApprovedMls} loading={loadingReports} />
        </div>

        <PersonDetailsModal
          show={editPersonModalOpen}
          personId={person.data?.attributes.id}
          modalType={person?.data?.attributes.status === "brivity-user" ? "user" : "lead"}
          onClose={() => {
            dispatch(actions.closeEditPersonModal());
            dispatch(actions.getPerson(person.data?.attributes.slug));
          }}
        />

        <HubspotRequestDemoModal show={state.platformCTAOpen} onClose={closePlatformCTA} />

        {state.showAddModal && (
          <AddMarketReportModal
            person={marketReportPerson}
            isOpen
            close={toggleAddMarketModal}
            primaryAgent={primaryAgent}
            id={state.editMarketReportId}
          />
        )}
        <ContactMergeModal push={push} />
      </div>
    </div>
  );
};

export default PersonDetail;
